// routes/settings.tsx
// -----------------------------------------------------------------------------
// Settings page - matches mobile settings layout with TinyClub Plan,
// Club Payments, Account & Support, and Sign Out sections.
// -----------------------------------------------------------------------------

import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition, useHeaderConfig } from "@/components/layout";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useAuth } from "@/components/providers/AuthProvider";
import {
  useFetchCurrentUserProfileQuery,
  useFetchUserSubscribedClubsQuery,
  useFetchBlockedUsersQuery,
  useFetchUserProfilesByIdsQuery,
  useUnblockUserMutation,
  useCheckIsAdminQuery,
  type UserProfile,
} from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/settings")({
  beforeLoad: requireAuth,
  component: SettingsPage,
});

function SettingsPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: profile } = useFetchCurrentUserProfileQuery(
    user?.id || "",
    { skip: !user?.id }
  );

  const {
    data: subscribedClubs,
    isLoading: isLoadingClubs,
  } = useFetchUserSubscribedClubsQuery(user?.id || "", {
    skip: !user?.id,
  });

  // Admin check (for moderation link)
  const { data: isAdmin } = useCheckIsAdminQuery(user?.id || "", {
    skip: !user?.id,
  });

  // Blocked users
  const { data: blockedUsers } = useFetchBlockedUsersQuery(user?.id || "", {
    skip: !user?.id,
  });
  const blockedUserIds = useMemo(
    () => (blockedUsers || []).map((b) => b.blocked_id).filter(Boolean),
    [blockedUsers]
  );
  const { data: blockedProfiles } = useFetchUserProfilesByIdsQuery(blockedUserIds, {
    skip: blockedUserIds.length === 0,
  });
  const [unblockUser] = useUnblockUserMutation();
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  // Configure global header
  useHeaderConfig(
    {
      breadcrumbs: [{ label: "Settings" }],
    },
    []
  );

  const membershipType = (profile as any)?.membership_type as
    | "pro"
    | "plus"
    | string
    | undefined;

  const handleOpenWebPaywall = async () => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-revenuecat-web-link`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: user?.id }),
        }
      );

      const data = await response.json();
      if (data?.url) {
        const paywallUrl = `${data.url}/${encodeURIComponent(user!.id)}`;
        window.open(paywallUrl, "_blank");
      }
    } catch (error) {
      console.error("Failed to open paywall:", error);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) return;

      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      await signOut();
    } catch (error) {
      console.error("Failed to delete account:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-6 pb-24">
          <div className="max-w-lg mx-auto space-y-8">
            {/* Membership banner */}
            {membershipType === "pro" && (
              <div className="bg-surface-1 rounded-2xl p-4 text-center">
                <p className="text-body-1-medium text-text-primary">
                  You're a TinyClub Pro!
                </p>
              </div>
            )}
            {membershipType === "plus" && (
              <div className="bg-surface-1 rounded-2xl p-4 text-center">
                <p className="text-body-1-medium text-text-primary">
                  Thanks for being part of TinyClub Plus!
                </p>
              </div>
            )}

            {/* TinyClub Plan Section */}
            <section>
              <h2 className="text-h6 text-text-primary mb-4">TinyClub plan</h2>
              <div className="bg-surface-1 rounded-2xl overflow-hidden">
                <button
                  className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                  onClick={handleOpenWebPaywall}
                >
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    {membershipType === "pro" || membershipType === "plus" ? (
                      <Icon
                        name="circle-check"
                        size={20}
                        className="text-[#3B82F6]"
                      />
                    ) : (
                      <Icon
                        name="circle-plus"
                        size={20}
                        className="text-[#6B7A3D]"
                      />
                    )}
                  </div>
                  <span className="text-body-1 text-text-primary flex-1">
                    {membershipType === "pro"
                      ? "Manage membership"
                      : membershipType === "plus"
                        ? "Upgrade to TinyClub Pro"
                        : "Upgrade to TinyClub Plus"}
                  </span>
                  <Icon
                    name="angle-right"
                    size={16}
                    className="text-text-secondary"
                  />
                </button>
              </div>
            </section>

            {/* Your Club Payments Section */}
            {(isLoadingClubs || (subscribedClubs && subscribedClubs.length > 0)) && (
              <section>
                <h2 className="text-h6 text-text-primary mb-1">
                  Your club payments
                </h2>
                <p className="text-body-3 text-text-secondary mb-4">
                  Your active club subscriptions.
                </p>

                {isLoadingClubs ? (
                  <div className="bg-surface-1 rounded-2xl p-4 flex items-center justify-center">
                    <Skeleton className="h-6 w-48" />
                  </div>
                ) : (
                  <div className="bg-surface-1 rounded-2xl overflow-hidden">
                    {subscribedClubs?.map((club, index) => (
                      <div key={club.id}>
                        {index > 0 && (
                          <div className="h-px bg-border-subtle ml-12" />
                        )}
                        <button
                          className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                          onClick={() =>
                            navigate({
                              to: "/settings/club-subscription/$clubId",
                              params: { clubId: club.id! },
                            })
                          }
                        >
                          <div className="w-6 flex-shrink-0 flex items-center justify-center">
                            <Icon
                              name="globe"
                              size={20}
                              className="text-text-primary"
                            />
                          </div>
                          <span className="text-body-1 text-text-primary flex-1">
                            {club.name}
                          </span>
                          <Icon
                            name="angle-right"
                            size={16}
                            className="text-text-secondary"
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Moderation Section (admin only) */}
            {isAdmin && (
              <section>
                <h2 className="text-h6 text-text-primary mb-4">Moderation</h2>
                <div className="bg-surface-1 rounded-2xl overflow-hidden">
                  <button
                    className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                    onClick={() => navigate({ to: "/moderation" })}
                  >
                    <div className="w-6 flex-shrink-0 flex items-center justify-center">
                      <Icon
                        name="shield"
                        size={20}
                        className="text-text-primary"
                      />
                    </div>
                    <span className="text-body-1 text-text-primary flex-1">
                      Moderation
                    </span>
                    <Icon
                      name="angle-right"
                      size={16}
                      className="text-text-secondary"
                    />
                  </button>
                </div>
              </section>
            )}

            {/* Account & Support Section */}
            <section>
              <h2 className="text-h6 text-text-primary mb-4">
                Account & support
              </h2>
              <div className="bg-surface-1 rounded-2xl overflow-hidden">
                {/* Contact support */}
                <button
                  className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                  onClick={() => {
                    window.location.href = "mailto:team@tiny.club";
                  }}
                >
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    <Icon
                      name="envelope"
                      size={20}
                      className="text-text-primary"
                    />
                  </div>
                  <span className="text-body-1 text-text-primary flex-1">
                    Contact support
                  </span>
                </button>

                <div className="h-px bg-border-subtle ml-12" />

                {/* Legal */}
                <button
                  className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                  onClick={() => navigate({ to: "/settings/legal" })}
                >
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    <Icon
                      name="scroll"
                      size={20}
                      className="text-text-primary"
                    />
                  </div>
                  <span className="text-body-1 text-text-primary flex-1">
                    Legal
                  </span>
                  <Icon
                    name="angle-right"
                    size={16}
                    className="text-text-secondary"
                  />
                </button>

                <div className="h-px bg-border-subtle ml-12" />

                {/* Delete account */}
                <button
                  className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    <Icon
                      name="trash"
                      size={20}
                      className="text-text-primary"
                    />
                  </div>
                  <span className="text-body-1 text-text-primary flex-1">
                    Delete account
                  </span>
                </button>
              </div>
            </section>

            {/* Blocked Users Section */}
            {blockedUserIds.length > 0 && (
              <section>
                <h2 className="text-h6 text-text-primary mb-4">
                  Blocked users
                </h2>
                <div className="bg-surface-1 rounded-2xl overflow-hidden">
                  {blockedUserIds.map((blockedId, index) => {
                    const profile = blockedProfiles?.find((p) => p.id === blockedId);
                    const displayName = profile ? userDisplayName(profile) : "Unknown user";
                    const avatar = profile?.avatar_small || profile?.avatar_medium;
                    return (
                      <div key={blockedId}>
                        {index > 0 && <div className="h-px bg-border-subtle ml-12" />}
                        <div className="flex items-center gap-3 p-4">
                          <div className="w-8 h-8 rounded-full overflow-hidden bg-surface-2 flex-shrink-0">
                            {avatar ? (
                              <img
                                src={avatar}
                                alt={displayName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Icon name="user" size={14} className="text-text-tertiary" />
                              </div>
                            )}
                          </div>
                          <span className="text-body-1 text-text-primary flex-1">
                            {displayName}
                          </span>
                          <button
                            onClick={async () => {
                              if (!user?.id) return;
                              setUnblockingId(blockedId);
                              try {
                                await unblockUser({ blockerId: user.id, blockedId }).unwrap();
                              } catch (error) {
                                console.error("Failed to unblock user:", error);
                              } finally {
                                setUnblockingId(null);
                              }
                            }}
                            disabled={unblockingId === blockedId}
                            className="px-3 py-1.5 rounded-full bg-surface-2 text-body-2-medium text-text-secondary hover:bg-surface transition-colors disabled:opacity-50"
                          >
                            {unblockingId === blockedId ? "..." : "Unblock"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Sign Out Section */}
            <section>
              <div className="bg-surface-1 rounded-2xl overflow-hidden">
                <button
                  className="flex items-center gap-3 p-4 w-full hover:bg-surface-2 transition-colors cursor-pointer text-left"
                  onClick={handleSignOut}
                >
                  <div className="w-6 flex-shrink-0 flex items-center justify-center">
                    <Icon
                      name="arrow-right-from-bracket"
                      size={20}
                      className="text-text-primary"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-body-1 text-text-primary block">
                      Sign out
                    </span>
                    {profile?.email && (
                      <span className="text-body-3 text-text-secondary">
                        {profile.email}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            </section>

            {/* Footer */}
            <div className="text-center space-y-1">
              <p className="text-body-3 text-text-tertiary">
                TinyClub Web &copy; {new Date().getFullYear()}
              </p>
              <p className="text-body-3 text-text-tertiary">Version 1.0.0</p>
            </div>
          </div>
        </div>
      </PageTransition>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete your account? This action cannot be
              undone. All your data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-red text-white hover:bg-red/90"
            >
              {isDeleting ? "Deleting..." : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
